import { useMemo } from 'react';
import { Star, X } from 'lucide-react';
import { useSocial } from '../hooks/useSocial';
import { useCollection } from '../hooks/useCollection';
import { getVariantConfig } from '../utils/cardVariants';

const FALLBACK_IMG =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA0OCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxjaXJjbGUgY3g9IjI0IiBjeT0iMzIiIHI9IjEwIiBmaWxsPSIjRDFENURCIi8+Cjwvc3ZnPg==';

const PRIORITY_CYCLE = { high: 'medium', medium: 'low', low: 'high' };

const PRIORITY_STYLES = {
  high: 'bg-danger-bg border-danger-border text-brick',
  medium: 'bg-yellow/15 border-yellow/40 text-yellow-dark',
  low: 'bg-cream-bg border-border text-ink-secondary'
};

const PRIORITY_LABELS = { high: 'High', medium: 'Med', low: 'Low' };

const WishlistView = () => {
  const { wishlistItems, wishlistLoaded, setWishlistPriority, removeFromWishlist } = useSocial();
  const { getVariantQuantity } = useCollection();

  // Group by set, keep the server's priority-first order within each group
  const groups = useMemo(() => {
    const bySet = {};
    for (const item of wishlistItems) {
      const key = item.setName || item.setId || 'Unknown set';
      if (!bySet[key]) bySet[key] = [];
      bySet[key].push(item);
    }
    return Object.entries(bySet).sort((a, b) => a[0].localeCompare(b[0]));
  }, [wishlistItems]);

  if (!wishlistLoaded) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-navy-dark border-t-transparent mb-4"></div>
        <span className="text-ink-secondary text-sm">Loading your wishlist...</span>
      </div>
    );
  }

  if (wishlistItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center px-6">
        <div className="w-14 h-14 rounded-full bg-yellow/15 border border-yellow/30 flex items-center justify-center mb-4">
          <Star size={24} className="text-yellow-dark" />
        </div>
        <h2 className="font-heading font-bold text-lg text-ink-primary mb-1">Your wishlist is empty</h2>
        <p className="text-ink-secondary text-sm max-w-xs">
          Open any card and tap the star on the version you're hunting for — Regular, Reverse Holo,
          whichever you're missing.
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-10 py-6">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-bold text-xl text-ink-primary">Wishlist</h2>
        <span className="text-[12px] text-ink-secondary">
          {wishlistItems.length} item{wishlistItems.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="space-y-6">
        {groups.map(([setName, items]) => (
          <div key={setName}>
            <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-muted mb-2">
              {setName}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {items.map((item) => {
                const owned = getVariantQuantity(item.cardId, item.variant) > 0;
                const config = getVariantConfig(item.variant);

                return (
                  <div
                    key={`${item.cardId}|${item.variant}`}
                    className="bg-white border border-border rounded-xl p-3 flex items-center gap-3"
                  >
                    <img
                      src={item.cardImage || FALLBACK_IMG}
                      alt={item.cardName}
                      className="w-10 h-14 object-contain rounded flex-shrink-0"
                      onError={(e) => {
                        e.target.src = FALLBACK_IMG;
                      }}
                    />

                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-ink-primary truncate">
                        {item.cardName || item.cardId}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span
                          className={`${config.color} text-white text-[9px] font-bold rounded px-1 py-0.5 flex-shrink-0`}
                        >
                          {config.short}
                        </span>
                        <span className="text-[11px] text-ink-secondary truncate">
                          #{item.cardNumber}
                          {item.rarity ? ` • ${item.rarity}` : ''}
                        </span>
                      </div>
                      {owned && (
                        <div className="text-[11px] text-success-green font-medium mt-0.5">
                          ✓ You now own this
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => setWishlistPriority(item, PRIORITY_CYCLE[item.priority])}
                      title="Tap to change priority"
                      className={`text-[10px] font-bold px-2 py-1 rounded-full border flex-shrink-0 transition-colors ${
                        PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.medium
                      }`}
                    >
                      {PRIORITY_LABELS[item.priority] || item.priority}
                    </button>

                    <button
                      onClick={() => removeFromWishlist(item.cardId, item.variant)}
                      title="Remove from wishlist"
                      className="w-7 h-7 rounded-full flex items-center justify-center text-ink-faint hover:text-brick hover:bg-danger-bg transition-colors flex-shrink-0"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WishlistView;
